import React, { createContext, useState, useContext, useEffect } from 'react';

const SubscriptionContext = createContext();

export const useSubscriptions = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }) => {
  // Initialize subscriptions from localStorage or default values
  const defaultSubscriptions = {
    cities: {
      'Vilnius': {
        subscribed: true,
        categories: {
          safety: true,
          traffic: true,
          utility: true,
          transport: true,
          events: true
        }
      },
      'Kaunas': {
        subscribed: true,
        categories: {
          safety: true,
          traffic: false,
          utility: true,
          transport: true,
          events: true
        }
      },
      'Klaipeda': {
        subscribed: false,
        categories: {
          safety: false,
          traffic: false,
          utility: false,
          transport: false,
          events: false
        }
      }
    }
  };

  const [subscriptions, setSubscriptions] = useState(() => {
    // Try to load from localStorage
    const saved = localStorage.getItem('userSubscriptions');
    if (saved) {
      const parsedSubscriptions = JSON.parse(saved);
      
      // Check if we need to migrate old data to include events category
      let needsMigration = false;
      for (const cityName in parsedSubscriptions.cities) {
        if (!parsedSubscriptions.cities[cityName].categories.hasOwnProperty('events')) {
          needsMigration = true;
          break;
        }
      }
      
      if (needsMigration) {
        // Add events category to existing subscriptions
        for (const cityName in parsedSubscriptions.cities) {
          parsedSubscriptions.cities[cityName].categories.events = 
            parsedSubscriptions.cities[cityName].subscribed;
        }
        // Save the migrated data
        localStorage.setItem('userSubscriptions', JSON.stringify(parsedSubscriptions));
      }
      
      return parsedSubscriptions;
    }
    return defaultSubscriptions;
  });

  // Save to localStorage whenever subscriptions change
  useEffect(() => {
    localStorage.setItem('userSubscriptions', JSON.stringify(subscriptions));
  }, [subscriptions]);

  // Check if user is subscribed to a specific city
  const isSubscribedToCity = (cityName) => {
    return subscriptions.cities[cityName]?.subscribed || false;
  };

  // Check if user is subscribed to a specific category in a city
  const isSubscribedToCityCategory = (cityName, category) => {
    const citySubscription = subscriptions.cities[cityName];
    if (!citySubscription || !citySubscription.subscribed) return false;
    return citySubscription.categories[category] || false;
  };

  // Toggle city subscription
  const toggleCitySubscription = (cityName) => {
    setSubscriptions(prev => ({
      ...prev,
      cities: {
        ...prev.cities,
        [cityName]: {
          ...prev.cities[cityName],
          subscribed: !prev.cities[cityName].subscribed
        }
      }
    }));
  };

  // Toggle city category subscription
  const toggleCityCategorySubscription = (cityName, category) => {
    setSubscriptions(prev => ({
      ...prev,
      cities: {
        ...prev.cities,
        [cityName]: {
          ...prev.cities[cityName],
          categories: {
            ...prev.cities[cityName].categories,
            [category]: !prev.cities[cityName].categories[category]
          }
        }
      }
    }));
  };

  return (
    <SubscriptionContext.Provider
      value={{
        subscriptions,
        isSubscribedToCity,
        isSubscribedToCityCategory,
        toggleCitySubscription,
        toggleCityCategorySubscription
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
